import { useEffect } from "react";
import SEO from "@/components/SEO";
import { Shield, Globe, FileCheck, Building2, Scale, Lock } from "lucide-react";

export default function LegalPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="legal-page" className="pt-32 pb-20 lg:pt-36">
      <SEO page="legal" />
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <h1 className="text-3xl sm:text-4xl font-bold text-[#0A1128] tracking-tight mb-2">Conformite reglementaire</h1>
        <p className="text-sm text-[#475569] mb-10">Agrements, licences et cadre reglementaire</p>

        <div className="space-y-12">
          {/* Swiss Regulation */}
          <section className="bg-[#F8F9FA] rounded-xl border border-slate-200 p-8">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-11 h-11 rounded-lg bg-[#1B2B5B]/5 flex items-center justify-center shrink-0">
                <Building2 size={22} className="text-[#1B2B5B]" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-[#0A1128]">Reglementation suisse</h2>
                <p className="text-sm text-[#475569] mt-1">Cadre legal et supervision en Suisse</p>
              </div>
            </div>
            <div className="prose prose-sm text-[#475569] leading-relaxed space-y-3">
              <p>AyaPay opere en conformite avec le cadre reglementaire suisse des services de paiement :</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>FINMA</strong> — Autorite federale de surveillance des marches financiers : AyaPay exerce ses activites en conformite avec la Loi sur les services financiers (LSFin) et la Loi sur les etablissements financiers (LEFin).</li>
                <li><strong>Loi sur le blanchiment d'argent (LBA)</strong> — Conformite aux obligations de diligence raisonnable, d'identification des clients (KYC) et de signalement des transactions suspectes au Bureau de communication en matiere de blanchiment d'argent (MROS).</li>
                <li><strong>Loi federale sur la protection des donnees (LPD)</strong> — Traitement des donnees personnelles conforme a la nouvelle LPD entree en vigueur le 1er septembre 2023, incluant la designation d'un conseiller a la protection des donnees.</li>
                <li><strong>Ordonnance sur les services de paiement</strong> — Respect des exigences operationnelles et de securite pour les prestataires de services de paiement en Suisse.</li>
              </ul>
            </div>
          </section>

          {/* European Regulation */}
          <section className="bg-[#F8F9FA] rounded-xl border border-slate-200 p-8">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-11 h-11 rounded-lg bg-[#1B2B5B]/5 flex items-center justify-center shrink-0">
                <Globe size={22} className="text-[#1B2B5B]" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-[#0A1128]">Reglementation europeenne</h2>
                <p className="text-sm text-[#475569] mt-1">Conformite avec les directives et reglements de l'UE</p>
              </div>
            </div>
            <div className="prose prose-sm text-[#475569] leading-relaxed space-y-3">
              <p>Nos services de paiement sont conformes aux reglementations europeennes suivantes :</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>PSD2 (Directive sur les services de paiement 2)</strong> — Directive (UE) 2015/2366 : Conformite avec les exigences d'authentification forte du client (SCA), de transparence des frais et de securite des transactions electroniques.</li>
                <li><strong>RGPD (Reglement General sur la Protection des Donnees)</strong> — Reglement (UE) 2016/679 : Traitement des donnees personnelles avec consentement, droit a l'oubli, portabilite et notification des violations dans les 72 heures.</li>
                <li><strong>Directive anti-blanchiment (AMLD6)</strong> — 6eme directive anti-blanchiment de l'UE : Due diligence renforcee, identification des beneficiaires effectifs et cooperation avec les autorites de surveillance.</li>
                <li><strong>Reglement sur les frais d'interchange (IFR)</strong> — Reglement (UE) 2015/751 : Plafonnement des frais d'interchange pour les transactions par carte au sein de l'EEE (0.2% debit, 0.3% credit).</li>
                <li><strong>EMD2 (Directive sur la monnaie electronique)</strong> — Directive 2009/110/CE : Cadre pour l'emission et la gestion de monnaie electronique.</li>
              </ul>
            </div>
          </section>

          {/* PCI DSS */}
          <section className="bg-[#F8F9FA] rounded-xl border border-slate-200 p-8">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-11 h-11 rounded-lg bg-[#00C853]/10 flex items-center justify-center shrink-0">
                <Shield size={22} className="text-[#00C853]" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-[#0A1128]">Certifications de securite</h2>
                <p className="text-sm text-[#475569] mt-1">Normes et certifications de securite des paiements</p>
              </div>
            </div>
            <div className="prose prose-sm text-[#475569] leading-relaxed space-y-3">
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>PCI-DSS Niveau 1</strong> — Plus haut niveau de certification de securite pour les donnees de cartes de paiement. Audit annuel par un evaluateur de securite qualifie (QSA) independant.</li>
                <li><strong>PCI PTS 6</strong> — Tous nos terminaux sont conformes a la norme PCI PTS version 6, le dernier standard de securite pour les terminaux de paiement physiques.</li>
                <li><strong>P2PE (Point-to-Point Encryption)</strong> — Chiffrement de bout en bout des donnees de paiement depuis le terminal jusqu'aux serveurs de traitement, rendant les donnees inutilisables en cas d'interception.</li>
                <li><strong>3D Secure 2.0</strong> — Authentification forte du client pour les transactions en ligne, reduisant la fraude tout en maintenant une experience utilisateur fluide.</li>
                <li><strong>Tokenisation</strong> — Les donnees sensibles des cartes sont remplacees par des tokens uniques, eliminant le stockage de donnees de cartes dans les systemes du Commercant.</li>
              </ul>
            </div>
          </section>

          {/* Licenses & Partnerships */}
          <section className="bg-[#F8F9FA] rounded-xl border border-slate-200 p-8">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-11 h-11 rounded-lg bg-[#1B2B5B]/5 flex items-center justify-center shrink-0">
                <FileCheck size={22} className="text-[#1B2B5B]" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-[#0A1128]">Licences et agrements</h2>
                <p className="text-sm text-[#475569] mt-1">Autorisations officielles pour les services de paiement</p>
              </div>
            </div>
            <div className="prose prose-sm text-[#475569] leading-relaxed space-y-3">
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Agrément acquéreur</strong> — AyaPay est partenaire d'un acquéreur agréé disposant d'une licence d'établissement de paiement délivrée par la Banque Centrale Néerlandaise (DNB) et autorisé à exercer dans l'ensemble de l'Espace Économique Européen et en Suisse.</li>
                <li><strong>Membre principal des schemas de paiement</strong> — Accréditation en tant que membre principal auprès de Visa, Mastercard, American Express, Discover et autres réseaux de paiement internationaux.</li>
                <li><strong>Licence Twint</strong> — Partenaire officiel pour l'acceptation des paiements Twint en Suisse.</li>
                <li><strong>Intermediaire financier enregistre</strong> — Inscription aupres d'un organisme d'autoregulation (OAR) reconnu par la FINMA pour les activites d'intermediation financiere.</li>
              </ul>
            </div>
          </section>

          {/* Data Protection */}
          <section className="bg-[#F8F9FA] rounded-xl border border-slate-200 p-8">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-11 h-11 rounded-lg bg-[#1B2B5B]/5 flex items-center justify-center shrink-0">
                <Lock size={22} className="text-[#1B2B5B]" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-[#0A1128]">Protection des donnees</h2>
                <p className="text-sm text-[#475569] mt-1">Politique de confidentialite et traitement des donnees</p>
              </div>
            </div>
            <div className="prose prose-sm text-[#475569] leading-relaxed space-y-3">
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Localisation des donnees</strong> — Les donnees de paiement sont traitees et stockees exclusivement dans des centres de donnees situes en Suisse et dans l'Union Europeenne, certifies ISO 27001.</li>
                <li><strong>Retention des donnees</strong> — Les donnees de transaction sont conservees conformement aux obligations legales (10 ans pour les donnees comptables en Suisse) et supprimees a l'expiration du delai legal.</li>
                <li><strong>Droits des personnes concernees</strong> — Droit d'acces, de rectification, d'effacement, de portabilite et d'opposition conformement a la LPD et au RGPD.</li>
                <li><strong>Sous-traitants</strong> — Tous les sous-traitants de donnees sont soumis a des accords de traitement des donnees (DPA) conformes et font l'objet d'audits reguliers.</li>
                <li><strong>Notification des violations</strong> — En cas de violation de donnees, notification au Prepose federal a la protection des donnees (PFPDT) et aux personnes concernees dans les delais requis.</li>
              </ul>
            </div>
          </section>

          {/* Legal framework */}
          <section className="bg-[#F8F9FA] rounded-xl border border-slate-200 p-8">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-11 h-11 rounded-lg bg-[#1B2B5B]/5 flex items-center justify-center shrink-0">
                <Scale size={22} className="text-[#1B2B5B]" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-[#0A1128]">Cadre juridique</h2>
                <p className="text-sm text-[#475569] mt-1">Lois et reglements applicables</p>
              </div>
            </div>
            <div className="prose prose-sm text-[#475569] leading-relaxed space-y-3">
              <p><strong>Droit suisse applicable :</strong></p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Code des obligations (CO) — Art. 1 et suivants</li>
                <li>Loi federale sur les services financiers (LSFin) — RS 950.1</li>
                <li>Loi federale sur les etablissements financiers (LEFin) — RS 954.1</li>
                <li>Loi federale sur le blanchiment d'argent (LBA) — RS 955.0</li>
                <li>Loi federale sur la protection des donnees (LPD) — RS 235.1</li>
                <li>Loi federale sur la surveillance des marches financiers (LFINMA) — RS 956.1</li>
                <li>Ordonnance sur les services de paiement — RS 958.11</li>
              </ul>
              <p className="mt-4"><strong>Reglements europeens applicables :</strong></p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Reglement (UE) 2016/679 — RGPD</li>
                <li>Directive (UE) 2015/2366 — PSD2</li>
                <li>Reglement (UE) 2015/751 — IFR (Interchange Fee Regulation)</li>
                <li>Directive (UE) 2018/843 — AMLD5 / AMLD6</li>
                <li>Reglement (UE) 2021/1230 — Paiements transfrontaliers</li>
              </ul>
              <p className="mt-4"><strong>Juridiction :</strong> Tribunal competent du canton de Berne, Suisse. La Convention de Vienne (CVIM) est expressement exclue.</p>
            </div>
          </section>

          {/* Company info */}
          <section className="bg-white rounded-xl border border-slate-200 p-8">
            <h2 className="text-xl font-semibold text-[#0A1128] mb-4">Informations sur la societe</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-[#475569]">
              <div><strong className="text-[#0A1128]">Raison sociale :</strong> AyaPay by AYAPOS</div>
              <div><strong className="text-[#0A1128]">Siege social :</strong> Chemin des Lilas 12, 2503 Biel, Suisse</div>
              <div><strong className="text-[#0A1128]">Telephone :</strong> +41 76 200 36 12</div>
              <div><strong className="text-[#0A1128]">Email :</strong> contact@ayapay.ch</div>
              <div><strong className="text-[#0A1128]">Site web :</strong> ayapay.ch</div>
              <div><strong className="text-[#0A1128]">Canton :</strong> Berne</div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
